if (typeof navigator.vibrate === 'function') {
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const touchDevice = matchMedia('(any-pointer: coarse)');
  const actions = 'button, summary, [role="button"], a.om-btn, a.om-swatch, a.om-header__brand, a[data-card-link], a.om-ps__row--product, [data-sheet-open], [data-haptic]';
  const choices = 'input[type="radio"], input[type="checkbox"], select';
  const excluded = ':disabled, [aria-disabled="true"], [aria-busy="true"], [inert], [hidden], [data-haptic="off"]';
  let inputType = '';
  let lastPulse = -Infinity;

  function pulse(element, duration) {
    if (!touchDevice.matches || reducedMotion.matches || document.visibilityState !== 'visible') return;
    if (element.closest(excluded)) return;
    const now = performance.now();
    if (now - lastPulse < 100) return;
    try {
      if (navigator.vibrate(duration)) lastPulse = now;
    } catch {}
  }

  document.addEventListener('pointerdown', (event) => {
    if (event.isTrusted) inputType = event.isPrimary ? event.pointerType : '';
  }, { capture: true, passive: true });

  document.addEventListener('pointercancel', () => {
    inputType = '';
  }, { capture: true, passive: true });

  document.addEventListener('keydown', () => {
    inputType = 'keyboard';
  }, { capture: true, passive: true });

  document.addEventListener('click', (event) => {
    if (!event.isTrusted || event.button !== 0 || event.detail === 0) return;
    if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
    if ((event.pointerType || inputType) !== 'touch') return;
    const target = event.target;
    if (!(target instanceof Element) || target.closest(choices) || target.closest('label')) return;
    const action = target.closest(actions);
    if (!action) return;
    const primary = action.matches('[data-add-button], [data-bulk-add], .om-dock-buy, [data-haptic="primary"]');
    pulse(action, primary ? 18 : 10);
  }, { capture: true, passive: true });

  document.addEventListener('change', (event) => {
    if (!event.isTrusted || inputType !== 'touch') return;
    const target = event.target;
    if (target instanceof Element && target.matches(choices)) pulse(target, 8);
  }, { capture: true, passive: true });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') inputType = '';
  });
// The native switch workaround is for iPhone only. On iPad its overlay can
// consume navigation taps; keep the original controls as the touch targets.
} else if (/iPhone/.test(navigator.userAgent) && 'switch' in HTMLInputElement.prototype && navigator.maxTouchPoints > 0) {
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const touchDevice = matchMedia('(any-pointer: coarse)');
  const actions = 'button, summary, [role="button"], a.om-btn, a.om-swatch, a.om-header__brand, a[data-card-link], a.om-ps__row--product, [data-sheet-open], [data-haptic], label';
  const excluded = ':disabled, [aria-disabled="true"], [aria-busy="true"], [inert], [hidden], [data-haptic="off"]';
  const surfaces = new Map();
  const anchors = CSS.supports('anchor-name', '--om-haptic');
  let nextAnchor = 0;
  let refreshPending = false;

  function enabled(action) {
    if (!touchDevice.matches || reducedMotion.matches || action.closest(excluded)) return false;
    if (action instanceof HTMLLabelElement) {
      const control = action.control;
      return !!control?.matches('input[type="checkbox"], input[type="radio"]') &&
        !control.closest(excluded) && !(control.type === 'radio' && control.checked);
    }
    return true;
  }

  function attach(action) {
    if (surfaces.has(action) || action.matches('input, select, textarea')) return;
    if (action instanceof HTMLLabelElement && !action.control?.matches('input[type="checkbox"], input[type="radio"]')) return;
    if (action.parentElement?.closest(actions)) return;
    if (!(action instanceof HTMLLabelElement) && action.querySelector('a, button, input, select, textarea, summary, [role="button"]')) return;

    const surface = document.createElement('span');
    surface.setAttribute('data-om-haptic-surface', '');
    surface.setAttribute('aria-hidden', 'true');
    surface.style.cssText = 'position:absolute;z-index:1;display:block;';
    const root = surface.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = 'label{position:absolute;inset:0;display:block;cursor:inherit;border-radius:inherit;-webkit-tap-highlight-color:transparent}input{position:absolute;width:1px;height:1px;margin:0;opacity:0;pointer-events:none;appearance:auto;-webkit-appearance:auto}';
    const label = document.createElement('label');
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.tabIndex = -1;
    input.setAttribute('switch', '');
    label.append(input);
    root.append(style, label);
    let pointerType = '';

    label.addEventListener('pointerdown', event => {
      pointerType = event.isTrusted && event.isPrimary ? event.pointerType : '';
    }, { passive: true });
    label.addEventListener('pointercancel', () => { pointerType = ''; }, { passive: true });
    label.addEventListener('click', event => {
      event.stopPropagation();
      if (event.target !== label) return;
      if (!event.isTrusted || !enabled(action) || document.visibilityState !== 'visible') {
        event.preventDefault();
        return;
      }
      if (pointerType !== 'touch') event.preventDefault();
      const modifiers = {
        ctrlKey: event.ctrlKey, metaKey: event.metaKey,
        altKey: event.altKey, shiftKey: event.shiftKey, button: event.button
      };
      queueMicrotask(() => {
        if (!action.isConnected || !enabled(action)) return;
        if (root.activeElement === input) input.blur();
        action.focus({ preventScroll: true });
        if (Object.values(modifiers).some(Boolean)) {
          action.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, composed: true, view: window, ...modifiers }));
        } else {
          action.click();
        }
        scheduleRefresh();
      });
    });
    input.addEventListener('focus', () => {
      input.blur();
      if (action.isConnected && enabled(action)) action.focus({ preventScroll: true });
    });

    const computed = getComputedStyle(action);
    surface.style.borderRadius = computed.borderRadius;
    surface.style.clipPath = `inset(0 round ${computed.borderRadius})`;
    if (anchors) {
      const anchor = `--om-haptic-${++nextAnchor}`;
      action.style.anchorName = computed.anchorName === 'none' ? anchor : `${computed.anchorName}, ${anchor}`;
      surface.style.positionAnchor = anchor;
      surface.style.top = 'anchor(top)';
      surface.style.left = 'anchor(left)';
      surface.style.width = 'anchor-size(width)';
      surface.style.height = 'anchor-size(height)';
    }
    surfaces.set(action, surface);
    action.after(surface);
    resizeObserver.observe(action);
  }

  function refresh() {
    refreshPending = false;
    for (const [action, surface] of surfaces) {
      if (!action.isConnected || action.parentElement !== surface.parentElement) {
        surface.remove();
        surfaces.delete(action);
        resizeObserver.unobserve(action);
      }
    }
    for (const action of document.querySelectorAll(actions)) {
      attach(action);
      const surface = surfaces.get(action);
      if (surface) {
        const display = enabled(action) && action.getClientRects().length ? 'block' : 'none';
        if (surface.style.display !== display) surface.style.display = display;
        if (!anchors && display === 'block') {
          if (surface.offsetParent !== action.offsetParent) {
            surface.style.display = 'none';
            continue;
          }
          surface.style.left = `${action.offsetLeft}px`;
          surface.style.top = `${action.offsetTop}px`;
          surface.style.width = `${action.offsetWidth}px`;
          surface.style.height = `${action.offsetHeight}px`;
        }
      }
    }
  }

  function scheduleRefresh() {
    if (refreshPending) return;
    refreshPending = true;
    requestAnimationFrame(refresh);
  }

  const observer = new MutationObserver(records => {
    if (records.some(record => !record.target.hasAttribute?.('data-om-haptic-surface'))) scheduleRefresh();
  });
  const resizeObserver = new ResizeObserver(scheduleRefresh);
  function start() {
    resizeObserver.observe(document.body);
    observer.observe(document.body, {
      subtree: true, childList: true, attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'aria-busy', 'hidden', 'inert', 'data-haptic', 'checked', 'class', 'style']
    });
    document.addEventListener('change', scheduleRefresh, { capture: true, passive: true });
    document.addEventListener('load', scheduleRefresh, { capture: true, passive: true });
    if (!anchors) document.addEventListener('scroll', scheduleRefresh, { capture: true, passive: true });
    window.addEventListener('resize', scheduleRefresh, { passive: true });
    reducedMotion.addEventListener('change', scheduleRefresh);
    touchDevice.addEventListener('change', scheduleRefresh);
    scheduleRefresh();
  }
  // Surfaces are measured and inserted once the page has loaded, not while it renders its first screen.
  let started = false;
  function startOnce() { if (started) return; started = true; start(); }
  if (document.readyState === 'complete') startOnce();
  else { window.addEventListener('load', startOnce, { once: true }); setTimeout(startOnce, 4000); }
}
